import React, { useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let LABELS_LOADED = false;
async function loadMBLabelsOnce() {
  if (LABELS_LOADED) return;
  LABELS_LOADED = true;
  try {
    const res = await axios.get(`${API}/mb-config`);
    if (res.data?.labels) {
      localStorage.setItem('mb_labels', JSON.stringify(res.data.labels));
    }
    if (res.data?.mbs) {
      const activeMBs = res.data.mbs.filter(m => !m.hidden).map(m => m.id);
      localStorage.setItem('mb_list', JSON.stringify(activeMBs));
    }
    window.dispatchEvent(new Event('mb-labels-updated'));
  } catch {}
}

export function useAvailableMBs() {
  const [mbs, setMbs] = React.useState(() => {
    try {
      const cached = localStorage.getItem('mb_list');
      if (cached) return JSON.parse(cached);
    } catch {}
    return ['MB1', 'MB2', 'MB3', 'MB4', 'MB5', 'MB6'];
  });

  React.useEffect(() => {
    async function loadMBs() {
      try {
        const res = await axios.get(`${API}/mb-config`);
        if (res.data?.mbs) {
          const activeMBs = res.data.mbs.filter(m => !m.hidden).map(m => m.id);
          setMbs(activeMBs);
          localStorage.setItem('mb_list', JSON.stringify(activeMBs));
        }
      } catch {}
    }
    loadMBs();
    const listener = () => {
      try {
        const cached = localStorage.getItem('mb_list');
        if (cached) setMbs(JSON.parse(cached));
      } catch {}
    };
    window.addEventListener('mb-labels-updated', listener);
    return () => window.removeEventListener('mb-labels-updated', listener);
  }, []);

  return mbs;
}

/**
 * Small colored pill showing which MB Setup a customer belongs to.
 * Renders nothing when value is empty/null.
 *
 * Props:
 *   value: 'MB1' | 'MB2' | 'MB3' | 'MB4' | 'MB5' | 'MB6' | null
 *   size:  'xs' | 'sm' (default 'xs')
 */
const MB_COLORS = {
  MB1: 'bg-wa-hover text-wa-text-primary border-emerald-300',
  MB2: 'bg-wa-hover text-wa-text-primary border-sky-300',
  MB3: 'bg-wa-hover text-wa-text-primary border-amber-300',
  MB4: 'bg-wa-hover text-wa-text-primary border-fuchsia-300',
  MB5: 'bg-wa-hover text-wa-text-primary border-rose-300',
  MB6: 'bg-wa-hover text-wa-text-primary border-indigo-300',
};

const MBSetupBadge = ({ value, size = 'xs', className = '' }) => {
  // Fetch labels lazily on first badge render + refresh on custom event
  const [, force] = React.useReducer((x) => x + 1, 0);
  useEffect(() => {
    loadMBLabelsOnce();
    const listener = () => force();
    window.addEventListener('mb-labels-updated', listener);
    return () => window.removeEventListener('mb-labels-updated', listener);
  }, []);

  if (!value) return null;
  const colorClass = MB_COLORS[value] || 'bg-gray-100 text-gray-700 border-gray-300';
  const sizeClass =
    size === 'sm'
      ? 'text-[11px] px-2 py-0.5'
      : 'text-[9px] px-1.5 py-0.5';

  // Show custom label if configured (cached in localStorage from MBConfig page)
  let display = value;
  try {
    const cached = JSON.parse(localStorage.getItem('mb_labels') || '{}');
    if (cached[value]) display = cached[value];
  } catch {}

  return (
    <span
      className={`inline-flex items-center font-bold rounded-full border ${colorClass} ${sizeClass} ${className}`}
      data-testid={`mb-badge-${value}`}
      title={value}
    >
      {display}
    </span>
  );
};

export const MB_SETUPS = ['MB1', 'MB2', 'MB3', 'MB4', 'MB5', 'MB6'];

export default MBSetupBadge;
